<template>
  <div class="page-container">
    <PageHeader title="接入方管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openAddDialog">新增接入方</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="fetchAppList">刷新</el-button>
      </template>
    </PageHeader>

    <TablePanel>
      <el-table v-loading="loading" :data="appList" stripe>
        <el-table-column prop="appId" label="接入方标识" min-width="150">
          <template slot-scope="{ row }">
            <span class="qw-copy-text">{{ row.appId }}</span>
          </template>
        </el-table-column>
        <el-table-column label="密钥" min-width="240">
          <template slot-scope="{ row }">
            <div class="qw-app-list__secret">
              <span class="qw-copy-text">{{ visibleSecretId === row.id ? row.appSecret : maskSecret(row.appSecret) }}</span>
              <el-button type="text" size="mini" icon="el-icon-view" @click="toggleSecret(row)" />
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="handleCopy(row.appSecret)">复制</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="可见群" min-width="180">
          <template slot-scope="{ row }">
            <el-tooltip v-if="row.chatIds" :content="row.chatIds" placement="top">
              <span class="qw-text-ellipsis" style="display: inline-block; max-width: 160px">{{ row.chatIds }}</span>
            </el-tooltip>
            <span v-else class="qw-tag qw-tag--success">全部群</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-switch
              :value="row.status === 1"
              active-color="#10b981"
              @change="val => handleStatusChange(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.remark || '-' }}</template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="165">
          <template slot-scope="{ row }">{{ formatDateTime(row.updateTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center" class-name="aj-table-cell">
          <template slot-scope="{ row }">
            <!-- 操作列统一走 business-actions 的操作链接体系 -->
            <el-link class="aj-table-operation-link aj-table-operation-link--warning" :underline="false" @click="openEditDialog(row)">编辑</el-link>
            <el-link class="aj-table-operation-link aj-table-operation-link--info" :underline="false" @click="handleResetSecret(row)">重置密钥</el-link>
            <el-link class="aj-table-operation-link aj-table-operation-link--danger" :underline="false" @click="handleDelete(row)">删除</el-link>
          </template>
        </el-table-column>
      </el-table>
    </TablePanel>

    <!-- 新增接入方弹窗 -->
    <el-dialog title="新增接入方" :visible.sync="addDialogVisible" width="480px" :close-on-click-modal="false">
      <el-form ref="addForm" :model="addForm" :rules="addRules" label-width="90px" size="small">
        <el-form-item label="接入方标识" prop="appId">
          <el-input v-model.trim="addForm.appId" placeholder="如 interview-system（唯一）" />
        </el-form-item>
        <el-form-item label="密钥" prop="appSecret">
          <el-input v-model.trim="addForm.appSecret" placeholder="留空自动生成 40 位随机密钥" />
        </el-form-item>
        <el-form-item label="可见群" prop="chatIds">
          <el-input v-model.trim="addForm.chatIds" placeholder="群 chatId 逗号分隔，留空 = 全部群" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model.trim="addForm.remark" placeholder="接入方说明（可选）" />
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button size="small" @click="addDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitAdd">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 编辑接入方弹窗 -->
    <el-dialog title="编辑接入方" :visible.sync="editDialogVisible" width="480px" :close-on-click-modal="false">
      <el-form ref="editForm" :model="editForm" :rules="editRules" label-width="90px" size="small">
        <el-form-item label="接入方标识">
          <el-input :value="editForm.appId" disabled />
        </el-form-item>
        <el-form-item label="可见群" prop="chatIds">
          <el-input v-model.trim="editForm.chatIds" placeholder="群 chatId 逗号分隔，清空 = 全部群" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="editForm.statusBool" active-text="启用" :active-value="true" :inactive-value="false" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model.trim="editForm.remark" placeholder="接入方说明（可选）" />
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button size="small" @click="editDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitEdit">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 重置密钥结果弹窗：新密钥只展示这一次，提醒立即复制发给接入方 -->
    <el-dialog title="重置成功" :visible.sync="secretResultVisible" width="520px" :close-on-click-modal="false">
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="旧密钥已即刻吊销，请立即复制新密钥并发送给接入方"
        style="margin-bottom: 16px"
      />
      <div class="qw-app-list__secret-result qw-copy-text">{{ newSecret }}</div>
      <template slot="footer">
        <el-button size="small" type="primary" icon="el-icon-document-copy" @click="handleCopy(newSecret)">复制新密钥</el-button>
        <el-button size="small" @click="secretResultVisible = false">我已保存，关 闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getAppList, addApp, updateApp, resetAppSecret, deleteApp } from '@/api/appList'
import { formatDateTime, copyText } from '@/utils/format'

// 接入方管理：外部系统凭 appId/appSecret 调用会话存档接口（对应后端 /qw-admin/app/*）
export default {
    name: 'AppList',
    components: { PageHeader, TablePanel },
    data () {
        return {
            loading: false,
            submitting: false,
            appList: [],
            visibleSecretId: null,
            addDialogVisible: false,
            editDialogVisible: false,
            secretResultVisible: false,
            newSecret: '',
            addForm: { appId: '', appSecret: '', chatIds: '', remark: '' },
            editForm: { id: null, appId: '', chatIds: '', remark: '', statusBool: true },
            addRules: {
                appId: [{ required: true, message: '请输入接入方标识', trigger: 'blur' }]
            },
            editRules: {}
        }
    },
    created () {
        this.fetchAppList()
    },
    methods: {
        formatDateTime,
        // 密钥掩码：只露前 4 后 4 位
        maskSecret (secret) {
            if (!secret) return '-'
            if (secret.length <= 8) return '********'
            return `${secret.slice(0, 4)}${'*'.repeat(12)}${secret.slice(-4)}`
        },
        toggleSecret (row) {
            this.visibleSecretId = this.visibleSecretId === row.id ? null : row.id
        },
        async fetchAppList () {
            this.loading = true
            try {
                const res = await getAppList()
                this.appList = res.data || []
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.loading = false
            }
        },
        async handleCopy (text) {
            try {
                await copyText(text)
                this.$message.success('已复制到剪贴板')
            } catch (error) {
                this.$message.error('复制失败，请手动复制')
            }
        },
        openAddDialog () {
            this.addForm = { appId: '', appSecret: '', chatIds: '', remark: '' }
            this.addDialogVisible = true
            this.$nextTick(() => {
                this.$refs.addForm && this.$refs.addForm.clearValidate()
            })
        },
        submitAdd () {
            this.$refs.addForm.validate(async valid => {
                if (!valid) return
                this.submitting = true
                try {
                    await addApp(this.addForm)
                    this.$message.success('接入方创建成功')
                    this.addDialogVisible = false
                    this.fetchAppList()
                } catch (error) {
                    // 错误提示已由 request 拦截器统一弹出
                } finally {
                    this.submitting = false
                }
            })
        },
        openEditDialog (row) {
            this.editForm = {
                id: row.id,
                appId: row.appId,
                chatIds: row.chatIds || '',
                remark: row.remark || '',
                statusBool: row.status === 1
            }
            this.editDialogVisible = true
        },
        async submitEdit () {
            this.submitting = true
            try {
                await updateApp({
                    id: this.editForm.id,
                    chatIds: this.editForm.chatIds,
                    remark: this.editForm.remark,
                    status: this.editForm.statusBool ? 1 : 0
                })
                this.$message.success('已更新')
                this.editDialogVisible = false
                this.fetchAppList()
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.submitting = false
            }
        },
        // 启用/停用切换（update 接口仅传 id + status）
        handleStatusChange (row, enabled) {
            const status = enabled ? 1 : 0
            const action = enabled ? '启用' : '停用'
            this.$confirm(`确定${action}接入方「${row.appId}」吗？${enabled ? '' : '停用后其凭据立即失效'}`, '提示', { type: 'warning' })
                .then(async () => {
                    await updateApp({ id: row.id, status })
                    this.$message.success(`已${action}`)
                    this.fetchAppList()
                })
                .catch(() => {})
        },
        // 重置密钥：旧密钥即刻吊销，弹窗展示新密钥
        handleResetSecret (row) {
            this.$confirm(
                `重置后「${row.appId}」的旧密钥立即失效，使用旧密钥的系统将无法访问。确定重置吗？`,
                '重置密钥',
                { type: 'warning' }
            )
                .then(async () => {
                    const res = await resetAppSecret(row.id)
                    this.newSecret = res.data || ''
                    this.secretResultVisible = true
                    this.fetchAppList()
                })
                .catch(() => {})
        },
        handleDelete (row) {
            this.$confirm(
                `删除后「${row.appId}」的接入配置不可恢复，一般建议使用「停用」代替。确定删除吗？`,
                '删除接入方',
                { type: 'error', confirmButtonText: '确认删除', confirmButtonClass: 'el-button--danger' }
            )
                .then(async () => {
                    await deleteApp(row.id)
                    this.$message.success('已删除')
                    this.fetchAppList()
                })
                .catch(() => {})
        }
    }
}
</script>

<style lang="scss" scoped>
.qw-app-list__secret {
    display: flex;
    align-items: center;
    gap: 4px;

    span {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// 重置密钥结果弹窗的新密钥展示块：警示语义 dashed 提示块（新密钥只展示一次）
.qw-app-list__secret-result {
    padding: 14px 16px;
    background: var(--qw-warning-bg);
    border: 1px dashed var(--qw-warning);
    border-radius: 8px;
    word-break: break-all;
    line-height: 1.6;
    font-size: 13px;
    color: #b45309;
}
</style>
